/**
 * The canonical nutrient keys. Mirrors app/services/nutrients.py exactly —
 * change both or neither.
 *
 * Values are per 100 g (or 100 ml). A nutrient the source didn't report is an
 * ABSENT key, never 0, so "no data" never renders as a confident zero.
 */

export type NutrientKey =
  | 'kcal' | 'protein_g' | 'carbs_g' | 'fat_g' | 'fibre_g' | 'sugars_g'
  | 'added_sugars_g' | 'sat_fat_g' | 'trans_fat_g' | 'alcohol_g'
  | 'sodium_mg' | 'cholesterol_mg' | 'potassium_mg' | 'calcium_mg' | 'iron_mg'
  | 'magnesium_mg' | 'zinc_mg' | 'vit_a_ug' | 'vit_c_mg' | 'vit_d_ug'
  | 'vit_e_mg' | 'vit_k_ug' | 'b12_ug' | 'folate_ug'

export type Nutrients = Partial<Record<NutrientKey, number>>

export const NUTRIENTS: Record<NutrientKey, { label: string; unit: string; ceiling: number }> = {
  kcal: { label: 'Calories', unit: 'kcal', ceiling: 900 },
  protein_g: { label: 'Protein', unit: 'g', ceiling: 100 },
  carbs_g: { label: 'Carbs', unit: 'g', ceiling: 100 },
  fat_g: { label: 'Fat', unit: 'g', ceiling: 100 },
  fibre_g: { label: 'Fibre', unit: 'g', ceiling: 100 },
  sugars_g: { label: 'Sugars', unit: 'g', ceiling: 100 },
  added_sugars_g: { label: 'Added sugars', unit: 'g', ceiling: 100 },
  sat_fat_g: { label: 'Saturated fat', unit: 'g', ceiling: 100 },
  trans_fat_g: { label: 'Trans fat', unit: 'g', ceiling: 100 },
  alcohol_g: { label: 'Alcohol', unit: 'g', ceiling: 100 },
  sodium_mg: { label: 'Sodium', unit: 'mg', ceiling: 40000 },
  cholesterol_mg: { label: 'Cholesterol', unit: 'mg', ceiling: 5000 },
  potassium_mg: { label: 'Potassium', unit: 'mg', ceiling: 20000 },
  calcium_mg: { label: 'Calcium', unit: 'mg', ceiling: 5000 },
  iron_mg: { label: 'Iron', unit: 'mg', ceiling: 500 },
  magnesium_mg: { label: 'Magnesium', unit: 'mg', ceiling: 5000 },
  zinc_mg: { label: 'Zinc', unit: 'mg', ceiling: 500 },
  vit_a_ug: { label: 'Vitamin A', unit: 'µg', ceiling: 100000 },
  vit_c_mg: { label: 'Vitamin C', unit: 'mg', ceiling: 5000 },
  vit_d_ug: { label: 'Vitamin D', unit: 'µg', ceiling: 5000 },
  vit_e_mg: { label: 'Vitamin E', unit: 'mg', ceiling: 1000 },
  vit_k_ug: { label: 'Vitamin K', unit: 'µg', ceiling: 10000 },
  b12_ug: { label: 'Vitamin B12', unit: 'µg', ceiling: 5000 },
  folate_ug: { label: 'Folate', unit: 'µg', ceiling: 10000 },
}

export const KEYS = Object.keys(NUTRIENTS) as NutrientKey[]
export const MACRO_KEYS = ['protein_g', 'carbs_g', 'fat_g'] as const

const r3 = (v: number) => Math.round(v * 1000) / 1000

function num(v: unknown): number | null {
  if (v === null || v === undefined || typeof v === 'boolean' || v === '') return null
  const f = Number(v)
  return Number.isFinite(f) ? f : null
}

/** Keep known keys with finite, non-negative values; per-100 values over the
 *  plausibility ceiling are dropped (a unit mix-up), not clamped. */
export function clean(values: unknown, per100 = true): Nutrients {
  const out: Nutrients = {}
  if (!values || typeof values !== 'object') return out
  const src = values as Record<string, unknown>
  for (const k of KEYS) {
    const v = num(src[k])
    if (v === null || v < 0) continue
    if (per100 && v > NUTRIENTS[k].ceiling) continue
    out[k] = r3(v)
  }
  return out
}

export function scale(per100: Nutrients | null | undefined, grams: number | null | undefined): Nutrients {
  if (!per100 || grams === null || grams === undefined) return {}
  const f = grams / 100
  const out: Nutrients = {}
  for (const k of KEYS) {
    const v = per100[k]
    if (v !== undefined) out[k] = r3(v * f)
  }
  return out
}

export function add(into: Nutrients, more: Nutrients | null | undefined): Nutrients {
  if (!more) return into
  for (const k of KEYS) {
    const v = more[k]
    if (v !== undefined && Number.isFinite(v)) into[k] = r3((into[k] ?? 0) + v)
  }
  return into
}

export function sum(list: (Nutrients | null | undefined)[]): Nutrients {
  const t: Nutrients = {}
  for (const n of list) add(t, n)
  return t
}

export function per100From(totals: Nutrients, grams: number): Nutrients | null {
  if (!grams || grams <= 0) return null
  const f = 100 / grams
  const out: Nutrients = {}
  for (const k of KEYS) {
    const v = totals[k]
    if (v !== undefined) out[k] = r3(v * f)
  }
  return out
}

export function atwaterKcal(n: Nutrients): number {
  return 4 * (n.protein_g ?? 0) + 4 * (n.carbs_g ?? 0) + 9 * (n.fat_g ?? 0) + 7 * (n.alcohol_g ?? 0)
}

export type MacroColumns = {
  kcal: number; protein_g: number; carbs_g: number; fat_g: number; fibre_g: number | null
}

export function macroColumns(t: Nutrients): MacroColumns {
  const r1 = (v: number) => Math.round(v * 10) / 10
  return {
    kcal: r1(t.kcal ?? 0),
    protein_g: r1(t.protein_g ?? 0),
    carbs_g: r1(t.carbs_g ?? 0),
    fat_g: r1(t.fat_g ?? 0),
    fibre_g: t.fibre_g === undefined ? null : r1(t.fibre_g),
  }
}

/** "12", "12.5", "0.4" — as many decimals as the magnitude deserves. */
export function fmt(v: number | null | undefined, unit = ''): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const a = Math.abs(v)
  const s = a >= 100 ? Math.round(v).toString() : a >= 10 ? (Math.round(v * 10) / 10).toString()
    : (Math.round(v * 10) / 10).toString()
  return unit ? `${s} ${unit}` : s
}

export function fmtKcal(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return Math.round(v).toLocaleString('en-CA')
}
